import { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';
import { useSignatureStore } from '../store/signatureStore';

export type Factor = 'email' | 'sms';

interface OTPVerificationProps {
  signerId: string;
  email: string;
  phone?: string;
  /** Tous les contrôles demandés au signataire (double canal : e-mail puis SMS) */
  required: Factor[];
  /** Ceux qu'un code doit encore prouver : le lien reçu prouve déjà son canal */
  pending: Factor[];
  onVerified: () => void;
  onCancel: () => void;
}

const LABEL: Record<Factor, string> = { email: 'E-mail', sms: 'SMS' };

function OTPVerification({ signerId, email, phone, required, pending, onVerified, onCancel }: OTPVerificationProps) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [canResend, setCanResend] = useState(false);
  const [countdown, setCountdown] = useState(60);
  // Facteurs restant à prouver par un code : le premier est celui en cours
  const [remaining, setRemaining] = useState<Factor[]>(pending);
  const otpSentRef = useRef(false);
  // Les routes OTP exigent le jeton de session du signataire
  const sessionToken = useSignatureStore((state) => state.sessionToken);
  const current = remaining[0];

  useEffect(() => {
    // Envoi du premier code à l'ouverture (une seule fois, même avec React StrictMode)
    if (!otpSentRef.current) {
      otpSentRef.current = true;
      sendOTP();
    }
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
    setCanResend(true);
  }, [countdown]);

  const sendOTP = async () => {
    try {
      setLoading(true);
      setError(null);
      await api.post(`/signers/${signerId}/otp/send`, { token: sessionToken });
      setCountdown(60);
      setCanResend(false);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Erreur lors de l\'envoi du code');
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (code.length !== 6) {
      setError('Le code doit contenir 6 chiffres');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await api.post(`/signers/${signerId}/otp/verify`, { token: sessionToken, code });

      if (response.data.next_factor) {
        // Double canal : le code suivant est envoyé tout de suite
        setRemaining((list) => list.slice(1));
        setCode('');
        await sendOTP();
      } else {
        onVerified();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message;
      if (errorMessage?.includes('attempts')) {
        setError('Trop de tentatives échouées. Veuillez demander un nouveau code.');
      } else if (errorMessage?.includes('expired')) {
        setError('Le code a expiré. Veuillez demander un nouveau code.');
      } else {
        setError('Code incorrect. Veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    setCode('');
    sendOTP();
  };

  const maskRecipient = (value: string) => {
    if (!value) return '***';
    if (value.includes('@')) {
      const [local, domain] = value.split('@');
      if (!local || !domain) return '***';
      return `${local[0]}***${local[local.length - 1]}@${domain}`;
    }
    if (value.length < 4) return '***';
    return value.slice(0, -4).replace(/\d/g, '*') + value.slice(-4);
  };

  if (!current) return null;

  const recipient = current === 'sms' ? maskRecipient(phone ?? '') : maskRecipient(email);

  return (
    <div className="card">
      <h2 className="text-2xl font-bold text-center mb-2">Vérification d'identité</h2>

      {/* Double canal : où en est le signataire */}
      {required.length > 1 && (
        <ol className="mb-4 flex items-center justify-center gap-2 text-sm">
          {required.map((factor, index) => {
            const done = !remaining.includes(factor);
            const active = factor === current;
            return (
              <li key={factor} className="flex items-center gap-2">
                {index > 0 && <span className="text-gray-400">→</span>}
                <span className={done ? 'text-green-600 font-medium' : active ? 'text-blue-600 font-medium' : 'text-gray-400'}>
                  {index + 1}. {LABEL[factor]} {done && '✓'}
                </span>
              </li>
            );
          })}
        </ol>
      )}

      {required.length > 1 && !pending.includes('email') && (
        <p className="text-center text-sm text-green-700 mb-2">
          Votre e-mail est confirmé par le lien que vous avez ouvert.
        </p>
      )}

      <p className="text-center text-gray-600 mb-6">
        Un code à 6 chiffres vient d'être envoyé {current === 'sms' ? 'par SMS' : 'par e-mail'} au{' '}
        <strong>{recipient}</strong>
        {current === 'sms' && (
          <span className="block text-sm text-gray-500 mt-1">
            Votre téléphone peut vous proposer le code au-dessus du clavier : touchez-le pour le saisir sans quitter cette page.
          </span>
        )}
      </p>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">
            Code de vérification
          </label>
          <input
            id="otp"
            name="otp"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="000000"
            className="input text-center text-2xl tracking-widest"
            disabled={loading}
          />
        </div>

        <button
          onClick={verifyOTP}
          disabled={loading || code.length !== 6}
          className="btn btn-primary w-full"
        >
          {loading ? 'Vérification...' : 'Vérifier le code'}
        </button>

        <div className="text-center">
          {canResend ? (
            <button
              onClick={handleResend}
              disabled={loading}
              className="text-sm text-primary-600 hover:text-primary-700 underline"
            >
              Renvoyer le code
            </button>
          ) : (
            <p className="text-sm text-gray-500">
              Renvoyer le code dans {countdown} secondes
            </p>
          )}
        </div>

        <button onClick={onCancel} className="btn btn-secondary w-full">
          Annuler
        </button>
      </div>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-xs text-blue-800">
          <strong>Sécurité :</strong> Ce code est à usage unique et expire dans 10 minutes.
          Ne le partagez jamais avec quiconque.
        </p>
      </div>
    </div>
  );
}

export default OTPVerification;
